import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import BlogContentEditor from "./blogContentEditor";
import {
  Blogs_API,
  BlogTopic_API,
  Authors_API,
  ImageUpload_API,
  ImageDelete_API,
  Admin_API,
  BlogContent_API,
  BlogTemplate_API,
} from "./environment";
import { useAuth } from "../context/AuthContext";
import {
  FaTrash,
  FaPlus,
  FaFilter,
  FaEdit,
  FaTimes,
  FaSave,
  FaArrowRight,
} from "react-icons/fa";
import { GrDocumentText } from "react-icons/gr";
import { TbWorldShare } from "react-icons/tb";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";

export default function Blogs() {
  const { userData } = useAuth();

  // States
  const [blogs, setBlogs] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topicCategories, setTopicCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [activeTab, setActiveTab] = useState("draft"); // draft | complete | live

  // Modal & Form States
  const [showModal, setShowModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view | edit | add
  const [editBlog, setEditBlog] = useState({});
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [newTableOfContents, setNewTableOfContents] = useState("");
  const [newAuthorId, setNewAuthorId] = useState("");
  const [savedChaptersData, setSavedChaptersData] = useState([]); // state for saved chapters

  // Chapter editor states
  const [templates, setTemplates] = useState([]);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [currentChapterForm, setCurrentChapterForm] = useState({
    template_id: "",
    displayMainTitle: "",
    tabs: [],
    contentData: [],
  });

  // Filter States
  const [filters, setFilters] = useState({
    blog_id: [],
    blog_title: [],
    topic_name: [],
    date: [],
    createdBy: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const columnDisplay = {
    blog_id: "Blog ID",
    blog_title: "Title",
    topic_name: "Topic",
    date: "Date",
    createdBy: "Created By",
  };

  // Fetch Data
  useEffect(() => {
    fetchBlogs();
    fetchTopics();
    fetchAuthors();
    fetchUsers();
    fetchTemplates();
  }, [userData.access_token]);

  const fetchBlogs = async () => {
    try {
      const res = await fetch(`${Blogs_API}/getAll`, {
        headers: { Authorization: `Bearer ${userData.access_token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const normalized = data.map((b) => ({
        ...b,
        status: b.status || "draft",
        table_of_contents: Array.isArray(b.table_of_contents)
          ? b.table_of_contents
          : [],
        authors: Array.isArray(b.authors) ? b.authors : [],
      }));
      setBlogs(normalized);
    } catch (err) {
      Swal.fire("Error", "Failed to load blogs", "error");
    }
  };

  const fetchTopics = async () => {
    try {
      const res = await fetch(`${BlogTopic_API}/getAll`, {
        headers: { Authorization: `Bearer ${userData.access_token}` },
      });
      const data = await res.json();
      setTopics(data);
      const cats = [...new Set(data.map((t) => t.topic_type))].sort();
      setTopicCategories(cats);
    } catch (err) {}
  };

  const fetchAuthors = async () => {
    try {
      const res = await fetch(`${Authors_API}/getAll`, {
        headers: { Authorization: `Bearer ${userData.access_token}` },
      });
      const data = await res.json();
      setAuthors(data);
    } catch (err) {}
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${Admin_API}/getAll`, {
        headers: { Authorization: `Bearer ${userData.access_token}` },
      });
      const data = await res.json();
      setUsers(data);
    } catch (err) {}
  };

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${BlogTemplate_API}/getAll`, {
        headers: { Authorization: `Bearer ${userData.access_token}` },
      });
      if (!res.ok) throw new Error("Failed to load templates");
      const data = await res.json();
      // Ensure template_schema is parsed when string
      const parsed = (data || []).map((t) => {
        let schema = t.template_schema;
        if (typeof schema === "string") {
          try {
            schema = JSON.parse(schema);
          } catch (e) {
            schema = null;
          }
        }
        return { ...t, template_schema: schema };
      });
      setTemplates(parsed);
    } catch (err) {
      // non-blocking
    }
  };

  // Helpers
  const getTopicName = (id) =>
    topics.find((t) => t.topic_id === id)?.topic_name || "N/A";
  const getUsername = (id) => users.find((u) => u.id === id)?.username || "N/A";
  const getAuthorName = (id) =>
    authors.find((a) => a.author_id === id)?.author_name || "N/A";

  const getDistinctValues = (col) => {
    const values = blogs
      .filter((b) => b.status === activeTab)
      .map((b) => {
        if (col === "topic_name") return getTopicName(b.topic_id);
        if (col === "createdBy") return getUsername(b.createdBy);
        return b[col];
      });
    return [...new Set(values)].sort((a, b) =>
      col === "blog_id" ? a - b : String(a).localeCompare(String(b))
    );
  };

  const applyFilters = (item) => {
    return (
      (filters.blog_id.length === 0 ||
        filters.blog_id.includes(item.blog_id)) &&
      (filters.blog_title.length === 0 ||
        filters.blog_title.includes(item.blog_title)) &&
      (filters.topic_name.length === 0 ||
        filters.topic_name.includes(getTopicName(item.topic_id))) &&
      (filters.date.length === 0 ||
        filters.date.includes(item.date?.slice(0, 10))) &&
      (filters.createdBy.length === 0 ||
        filters.createdBy.includes(getUsername(item.createdBy)))
    );
  };

  const tabFiltered = blogs.filter((b) => b.status === activeTab);
  const filteredBlogs = tabFiltered.filter(applyFilters);
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);
  const paginatedBlogs = filteredBlogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedRows(
      selectedRows.length === filteredBlogs.length
        ? []
        : filteredBlogs.map((b) => b.blog_id)
    );
  };

  // Filter Popup
  const handleFilterClick = (col) => {
    setShowFilterPopup(col);
    setTempSelected(filters[col] || []);
    setSearchInput("");
  };

  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSel = tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prev) => ({ ...prev, [showFilterPopup]: newSel }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  const renderFilterPopup = (col) => {
    const vals = getDistinctValues(col).filter((v) =>
      v?.toString().toLowerCase().includes(searchInput.toLowerCase())
    );

    return (
      <div className="filter-popup">
        <div className="filter-header">
          <span>{columnDisplay[col]}</span>
          <span
            className="clear-filter"
            onClick={() => {
              setFilters((prev) => ({ ...prev, [col]: [] }));
              setShowFilterPopup(null);
            }}
          >
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="text"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="suggestions-list">
          {vals.map((v) => (
            <label key={v}>
              <input
                type="checkbox"
                checked={tempSelected.includes(v)}
                onChange={() =>
                  setTempSelected((prev) =>
                    prev.includes(v)
                      ? prev.filter((x) => x !== v)
                      : [...prev, v]
                  )
                }
              />
              {v ?? "N/A"}
            </label>
          ))}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  // Modal Functions
  const viewBlog = (blog) => {
    setSelectedBlog(blog);
    setEditBlog({
      ...blog,
      topic_type:
        topics.find((t) => t.topic_id === blog.topic_id)?.topic_type || "",
    });
    setModalMode("view");
    setShowModal(true);
  };

  const startEdit = () => setModalMode("edit");

  const closeModal = () => {
    setShowModal(false);
    setEditBlog({});
    setSelectedBlog(null);
    setNewTableOfContents("");
    setNewAuthorId("");
  };

  const addBlog = () => {
    const today = new Date().toISOString().slice(0, 10);
    setEditBlog({
      topic_id: "",
      topic_type: "",
      blog_title: "",
      authors: [],
      banner_image: "",
      intro_paragraph: "",
      blog_overview: "",
      table_of_contents: [],
      createdBy: userData.id,
      date: today,
      status: "draft",
    });
    setModalMode("add");
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditBlog((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "topic_type" ? { topic_id: "" } : {}),
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const valid = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!valid.includes(file.type)) {
      Swal.fire("Error", "Invalid image type", "error");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      if (editBlog.banner_image) {
        await fetch(
          `${ImageDelete_API}?path=${encodeURIComponent(
            editBlog.banner_image
          )}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${userData.access_token}` },
          }
        );
      }

      const res = await fetch(ImageUpload_API, {
        method: "POST",
        headers: { Authorization: `Bearer ${userData.access_token}` },
        body: formData,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setEditBlog((prev) => ({ ...prev, banner_image: data.path }));
      Swal.fire("Success", "Banner uploaded", "success");
    } catch (err) {
      Swal.fire("Error", "Upload failed", "error");
    }
  };

  const addTableOfContents = () => {
    if (newTableOfContents.trim()) {
      setEditBlog((prev) => ({
        ...prev,
        table_of_contents: [
          ...prev.table_of_contents,
          newTableOfContents.trim(),
        ],
      }));
      setNewTableOfContents("");
    }
  };

  const removeTableOfContents = (i) => {
    setEditBlog((prev) => ({
      ...prev,
      table_of_contents: prev.table_of_contents.filter((_, idx) => idx !== i),
    }));
  };

  const addAuthor = () => {
    if (newAuthorId) {
      setEditBlog((prev) => ({
        ...prev,
        authors: [...prev.authors, parseInt(newAuthorId)],
      }));
      setNewAuthorId("");
    }
  };

  const removeAuthor = (i) => {
    setEditBlog((prev) => ({
      ...prev,
      authors: prev.authors.filter((_, idx) => idx !== i),
    }));
  };

  const saveBlog = async () => {
    try {
      // Force status to "draft" when clicking Save
      const blogToSave = { ...editBlog, status: "draft" };

      const url =
        modalMode === "add"
          ? `${Blogs_API}/create`
          : `${Blogs_API}/update/${editBlog.blog_id}`;

      const res = await fetch(url, {
        method: modalMode === "add" ? "POST" : "PUT",
        headers: {
          Authorization: `Bearer ${userData.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(blogToSave),
      });

      if (!res.ok) throw new Error();

      Swal.fire("Saved!", "Blog saved as Draft", "success");
      setShowModal(false);
      fetchBlogs();
    } catch (err) {
      Swal.fire("Error", "Failed to save", "error");
    }
  };

  const publishToLive = async (id) => {
    Swal.fire({
      title: "Publish?",
      text: "Make this blog live on website?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Publish!",
    }).then(async (res) => {
      if (res.isConfirmed) {
        await fetch(`${Blogs_API}/update/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "live" }),
        });
        fetchBlogs();
        Swal.fire("Published!", "", "success");
      }
    });
  };

  const deleteBlog = async (id) => {
    const blog = blogs.find((b) => b.blog_id === id);
    if (!blog) return;

    Swal.fire({
      title: "Delete permanently?",
      icon: "warning",
      showCancelButton: true,
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        // Delete chapter images
        const contentRes = await fetch(`${BlogContent_API}/getByBlogId/${id}`, {
          headers: { Authorization: `Bearer ${userData.access_token}` },
        });
        if (contentRes.ok) {
          const contents = await contentRes.json();
          for (const c of contents) {
            if (c.images_paths) {
              for (const img of c.images_paths) {
                if (img.path) {
                  await fetch(
                    `${ImageDelete_API}?path=${encodeURIComponent(img.path)}`,
                    {
                      method: "DELETE",
                      headers: {
                        Authorization: `Bearer ${userData.access_token}`,
                      },
                    }
                  );
                }
              }
            }
          }
        }

        // Delete banner + content + blog
        if (blog.banner_image) {
          await fetch(
            `${ImageDelete_API}?path=${encodeURIComponent(blog.banner_image)}`,
            {
              method: "DELETE",
              headers: { Authorization: `Bearer ${userData.access_token}` },
            }
          );
        }
        await fetch(`${BlogContent_API}/deleteByBlogId/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${userData.access_token}` },
        });
        await fetch(`${Blogs_API}/delete/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${userData.access_token}` },
        });

        setBlogs((prev) => prev.filter((b) => b.blog_id !== id));
        Swal.fire("Deleted!", "", "success");
      } catch (err) {
        Swal.fire("Error", "Delete failed", "error");
      }
    });
  };

  const deleteSelectedRows = async () => {
    for (const id of selectedRows) await deleteBlog(id);
    setSelectedRows([]);
  };

  // Render Helpers
  const renderField = (field, label, type = "text") => (
    <tr>
      <td>{label}</td>
      <td>
        {modalMode === "edit" || modalMode === "add" ? (
          type === "textarea" ? (
            <textarea
              name={field}
              value={editBlog[field] || ""}
              onChange={handleChange}
              rows="4"
            />
          ) : (
            <input
              type={type}
              name={field}
              value={editBlog[field] || ""}
              onChange={handleChange}
            />
          )
        ) : (
          editBlog[field] || "N/A"
        )}
      </td>
    </tr>
  );

  const renderTopic = () => {
    const filtered = editBlog.topic_type
      ? topics.filter((t) => t.topic_type === editBlog.topic_type)
      : topics;

    return (
      <tr>
        <td>Topic</td>
        <td>
          {modalMode === "edit" || modalMode === "add" ? (
            <div>
              <select
                name="topic_type"
                value={editBlog.topic_type || ""}
                onChange={handleChange}
              >
                <option value="">Select Category</option>
                {topicCategories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select
                name="topic_id"
                value={editBlog.topic_id || ""}
                onChange={handleChange}
              >
                <option value="">Select Topic</option>
                {filtered.map((t) => (
                  <option key={t.topic_id} value={t.topic_id}>
                    {t.topic_name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            getTopicName(editBlog.topic_id)
          )}
        </td>
      </tr>
    );
  };

  const renderAuthors = () => (
    <tr>
      <td>Authors</td>
      <td>
        {modalMode === "edit" || modalMode === "add" ? (
          <div>
            <ul>
              {editBlog.authors?.map((id, i) => (
                <li key={i}>
                  {getAuthorName(id)}
                  <button onClick={() => removeAuthor(i)}>
                    <FaTimes />
                  </button>
                </li>
              )) || <li>No authors</li>}
            </ul>
            <select
              value={newAuthorId}
              onChange={(e) => setNewAuthorId(e.target.value)}
            >
              <option value="">Select Author</option>
              {authors.map((a) => (
                <option key={a.author_id} value={a.author_id}>
                  {a.author_name}
                </option>
              ))}
            </select>
            <button onClick={addAuthor}>
              <FaPlus />
            </button>
          </div>
        ) : (
          <ul>
            {editBlog.authors?.map((id, i) => (
              <li key={i}>{getAuthorName(id)}</li>
            )) || <li>No authors</li>}
          </ul>
        )}
      </td>
    </tr>
  );

  const renderBannerImage = () => {
    const url = editBlog.banner_image
      ? editBlog.banner_image.startsWith("http")
        ? editBlog.banner_image
        : `${Blogs_API.replace(/\/blogs$/, "")}${editBlog.banner_image}`
      : "";
    return (
      <tr>
        <td>Banner Image</td>
        <td>
          {modalMode === "edit" || modalMode === "add" ? (
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
              />
              {editBlog.banner_image && (
                <div style={{ marginTop: "10px" }}>
                  <img src={url} alt="banner" style={{ maxWidth: "500px" }} />
                  <p>
                    <small>{editBlog.banner_image}</small>
                  </p>
                </div>
              )}
            </div>
          ) : editBlog.banner_image ? (
            <img src={url} alt="banner" style={{ maxWidth: "500px" }} />
          ) : (
            "No image"
          )}
        </td>
      </tr>
    );
  };

  const renderTableOfContents = () => (
    <tr>
      <td>Table of Contents</td>
      <td>
        {modalMode === "edit" || modalMode === "add" ? (
          <div>
            <ul>
              {editBlog.table_of_contents?.map((e, i) => (
                <li key={i}>
                  {i + 1}. {e}
                  <button onClick={() => removeTableOfContents(i)}>
                    <FaTimes />
                  </button>
                </li>
              )) || <li>No chapters</li>}
            </ul>
            <input
              value={newTableOfContents}
              onChange={(e) => setNewTableOfContents(e.target.value)}
              placeholder="Chapter name"
            />
            <button onClick={addTableOfContents}>
              <FaPlus />
            </button>
          </div>
        ) : (
          <ol>
            {editBlog.table_of_contents?.map((e, i) => (
              <li key={i}>{e}</li>
            )) || <li>No chapters</li>}
          </ol>
        )}
      </td>
    </tr>
  );

  const goToContentEditor = async () => {
    try {
      // Step 1: Save blog as draft first
      const blogToSave = { ...editBlog, status: "draft" };

      const url =
        modalMode === "add"
          ? `${Blogs_API}/create`
          : `${Blogs_API}/update/${editBlog.blog_id}`;

      const res = await fetch(url, {
        method: modalMode === "add" ? "POST" : "PUT",
        headers: {
          Authorization: `Bearer ${userData.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(blogToSave),
      });

      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || "Save failed");
      }

      const savedBlog = await res.json();

      // If new blog, update editBlog with blog_id
      if (modalMode === "add") {
        setEditBlog({ ...savedBlog, status: "draft" });
      }

      // fetch existing chapters
      const contentRes = await fetch(
        `${BlogContent_API}/getByBlogId/${savedBlog.blog_id}`,
        {
          headers: { Authorization: `Bearer ${userData.access_token}` },
        }
      );
      let existingChapters = [];
      if (contentRes.ok) {
        existingChapters = await contentRes.json();
      }
      setSavedChaptersData(existingChapters);

      // Step 2: Hide main modal and open content editor
      setShowModal(false);
      setShowContentModal(true);
      setCurrentChapterIndex(0);
      setCurrentChapterForm({
        template_id: "",
        displayMainTitle: "",
        tabs: [],
        contentData: [],
      });

      Swal.fire(
        "Saved!",
        "Blog saved as Draft → Opening Content Editor",
        "success"
      );
    } catch (err) {
      Swal.fire("Error", "Failed to save before opening editor", "error");
    }
  };

  // Chapter navigation and save helpers
  const setSelectedTemplate = (id) => {
    setCurrentChapterForm((prev) => ({ ...prev, template_id: id }));
  };

  const checkAndCompleteBlogIfDone = async () => {
    try {
      const res = await fetch(
        `${BlogContent_API}/getByBlogId/${editBlog.blog_id}`,
        {
          headers: { Authorization: `Bearer ${userData.access_token}` },
        }
      );
      if (!res.ok) return;
      const list = await res.json();
      const totalChapters = Array.isArray(editBlog.table_of_contents)
        ? editBlog.table_of_contents.length
        : 0;
      if (list.length >= totalChapters && totalChapters > 0) {
        await fetch(`${Blogs_API}/update/${editBlog.blog_id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "complete" }),
        });
        fetchBlogs();
      }
    } catch (err) {
      // ignore
    }
  };

  const goToNextChapter = async () => {
    const chapters = editBlog.table_of_contents || [];
    const nextIndex = currentChapterIndex + 1;
    if (nextIndex < chapters.length) {
      setCurrentChapterIndex(nextIndex);
      setCurrentChapterForm({
        template_id: "",
        displayMainTitle: "",
        tabs: [],
        contentData: [],
      });
    } else {
      // finished all chapters
      setShowContentModal(false);
      await checkAndCompleteBlogIfDone();
      Swal.fire("Chapters complete", "All chapters saved.", "success");
    }
  };

  const handleChapterSaved = async () => {
    // after child saves, check for completion
    await checkAndCompleteBlogIfDone();
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Blogs - SMTS Admin Panel</title>
      </Helmet>

      {/* Tabs */}
      <div className="page-title" style={{ marginBottom: "20px" }}>
        {["draft", "complete", "live"].map((tab) => (
          <h2
            key={tab}
            className={activeTab === tab ? "active" : ""}
            onClick={() => {
              setActiveTab(tab);
              setCurrentPage(1);
              setSelectedRows([]);
              setFilters({
                blog_id: [],
                blog_title: [],
                topic_name: [],
                date: [],
                createdBy: [],
              });
            }}
            style={{
              cursor: "pointer",
              display: "inline-block",
              marginRight: "30px",
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)} Blogs
            {tab === "draft" && ""}
            {tab === "complete" && "(Ready)"}
            {tab === "live" && " (Published)"}
          </h2>
        ))}
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredBlogs.length &&
                    filteredBlogs.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              {["blog_id", "blog_title", "topic_name", "date", "createdBy"].map(
                (col) => (
                  <th key={col} style={{ position: "relative" }}>
                    {columnDisplay[col]}
                    <FaFilter
                      className={`filter-icon ${
                        filters[col].length > 0 ? "active-filter" : ""
                      }`}
                      onClick={() => handleFilterClick(col)}
                      style={{ marginLeft: "5px", cursor: "pointer" }}
                    />
                    {showFilterPopup === col && renderFilterPopup(col)}
                  </th>
                )
              )}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedBlogs.map((blog) => (
              <tr
                key={blog.blog_id}
                className={
                  selectedRows.includes(blog.blog_id) ? "selected" : ""
                }
                onDoubleClick={() => toggleRowSelection(blog.blog_id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(blog.blog_id)}
                    onChange={() => toggleRowSelection(blog.blog_id)}
                  />
                </td>
                <td>{blog.blog_id}</td>
                <td>{blog.blog_title}</td>
                <td>{getTopicName(blog.topic_id)}</td>
                <td>{blog.date?.slice(0, 10)}</td>
                <td>{getUsername(blog.createdBy)}</td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => viewBlog(blog)}
                    disabled={selectedRows.length > 1}
                  >
                    <GrDocumentText />
                  </button>
                  {activeTab === "complete" && (
                    <button
                      className="action-button"
                      onClick={() => publishToLive(blog.blog_id)}
                      title="Publish Live"
                    >
                      <TbWorldShare style={{ color: "#00d4aa" }} />
                    </button>
                  )}
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteBlog(blog.blog_id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(v) => {
          setItemsPerPage(v);
          setCurrentPage(1);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" /> Delete Selected ({selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addBlog}>
        <FaPlus />
      </button>

      {/* Modal */}
      {showModal && (
        <div className="job-modal-overlay">
          <div className="job-modal">
            <div className="job-modal-header">
              <h2>
                {modalMode === "add"
                  ? "Add New Blog"
                  : `(${editBlog.blog_id}) ${editBlog.blog_title || ""}`}
              </h2>
              <div className="job-modal-actions">
                {modalMode === "view" && (
                  <button onClick={startEdit}>
                    <FaEdit />
                  </button>
                )}
                {(modalMode === "edit" || modalMode === "add") && (
                  <>
                    <button onClick={saveBlog}>
                      <FaSave /> Save as Draft
                    </button>
                    {editBlog.table_of_contents?.length > 0 && (
                      <button onClick={goToContentEditor}>
                        <FaArrowRight /> Next
                      </button>
                    )}
                    <button onClick={closeModal}>
                      <FaTimes />
                    </button>
                  </>
                )}
                <button className="job-modal-close" onClick={closeModal}>
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="job-modal-body">
              <table className="job-details-table">
                <tbody>
                  {modalMode !== "add" && (
                    <tr>
                      <td>Blog ID</td>
                      <td>{editBlog.blog_id}</td>
                    </tr>
                  )}
                  {renderTopic()}
                  {renderField("blog_title", "Title")}
                  {renderAuthors()}
                  {renderBannerImage()}
                  {renderField(
                    "intro_paragraph",
                    "Intro Paragraph",
                    "textarea"
                  )}
                  {renderField("blog_overview", "Blog Overview", "textarea")}
                  {renderTableOfContents()}
                  <tr>
                    <td>Created By</td>
                    <td>{getUsername(editBlog.createdBy)}</td>
                  </tr>
                  <tr>
                    <td>Date</td>
                    <td>{editBlog.date?.slice(0, 10)}</td>
                  </tr>
                  {modalMode !== "add" && (
                    <tr>
                      <td>Status</td>
                      <td>
                        <strong
                          style={{
                            color:
                              editBlog.status === "live"
                                ? "green"
                                : editBlog.status === "complete"
                                ? "blue"
                                : "orange",
                          }}
                        >
                          {editBlog.status?.toUpperCase() || "DRAFT"}
                        </strong>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Content Editor Modal */}
      {showContentModal && (
        <div className="job-modal-overlay">
          <div
            className="job-modal"
            style={{ maxWidth: "1000px", width: "95%" }}
          >
            <div className="job-modal-header">
              <h2>
                Chapter {currentChapterIndex + 1} of{" "}
                {editBlog.table_of_contents?.length || 0} —{" "}
                {editBlog.table_of_contents?.[currentChapterIndex] || ""}
              </h2>
              <div className="job-modal-actions">
                <button
                  className="job-modal-close"
                  onClick={() => setShowContentModal(false)}
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="job-modal-body">
              <BlogContentEditor
                chapterName={
                  editBlog.table_of_contents?.[currentChapterIndex] || ""
                }
                templates={templates}
                formData={currentChapterForm}
                setFormData={setCurrentChapterForm}
                selectedTemplate={currentChapterForm.template_id}
                setSelectedTemplate={setSelectedTemplate}
                editBlog={editBlog}
                userData={userData}
                onSaved={handleChapterSaved}
                onNext={goToNextChapter}
                savedChapters={savedChaptersData}
                currentChapterIndex={currentChapterIndex}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
